import {
  interactiveTimeGroupPresenter,
  readingsToBillableSeconds,
} from 'infrastructure/services/interactiveTime';
import { convertSecondsToTime } from 'utils/timeHelper';
import useUser from 'utils/useUser';
import { Link } from 'react-router-dom';
import { isInteractiveTimeAssignAllowed } from 'constants/permissions';
import { DateFormatter } from 'infrastructure/functions';
import { useTranslation } from 'react-i18next';
import { getColSortDir } from 'infrastructure/hooks/interactiveTimeDashboard/useFilters';

import Assignee from './assignee';
import s from './style.module.scss';
import ContactAvailabilityCol from './contact-availability';
import { InteractionFlag } from './interaction-flag';

import type { IModifiedTableColumn } from 'infrastructure/antd/interfaces/i-modified-table-column';
import type { InteractiveTimeItem } from 'infrastructure/hooks/interactiveTimeDashboard/types';

export const useColumns = (): IModifiedTableColumn<InteractiveTimeItem>[] => {
  const { userType } = useUser();
  const showAssigneeFilter = isInteractiveTimeAssignAllowed(userType);
  const { t } = useTranslation();

  return [
    {
      title: t('labels.patientGuid'),
      key: 'patientGuid',
      render: (_, record) => (
        <Link
          to={`/patient/${record.patient.guid}`}
          className={s.link}
          data-cy="interactiveTimeDashboard-patientGuid"
        >
          {record.patient.guid}
        </Link>
      ),
    },
    {
      title: t('labels.contactAvailability'),
      key: 'contactAvailability',
      align: 'center',
      width: '105px',
      className: s.contact,
      render: (_, record) => <ContactAvailabilityCol record={record} />,
    },
    {
      title: t('labels.patientName'),
      key: 'patientFullName',
      render: (_, record) => (
        <span data-cy="interactiveTimeDashboard-patientFullName">
          {record.patient.fullName}
        </span>
      ),
      sorter: true,
      sortOrder: getColSortDir('name'),
      sortKey: 'name',
    },
    {
      title: t('labels.interactiveTimeGroup'),
      key: 'interactiveTimeGroup',
      render: (_, record) => (
        <span data-cy="interactiveTimeDashboard-interactiveTimeGroup">
          {interactiveTimeGroupPresenter(record.interactionTime ?? 0)}
        </span>
      ),
      sorter: true,
      sortOrder: getColSortDir('interactiveTimeGroup'),
    },
    {
      title: t('labels.criticalReadings'),
      key: 'criticalReadings',
      render: (_, record) => (
        <b data-cy="interactiveTimeDashboard-criticalReadingsCount">
          {record.readingsCount.critical}
        </b>
      ),
      sorter: true,
      sortOrder: getColSortDir('criticalReadings'),
    },
    {
      title: t('labels.atRiskReadings'),
      key: 'atRiskReadings',
      render: (_, record) => (
        <b data-cy="interactiveTimeDashboard-atRiskReadingsCount">
          {record.readingsCount.atRisk}
        </b>
      ),
      sorter: true,
      sortOrder: getColSortDir('atRiskReadings'),
    },
    {
      title: t('labels.normalReadings'),
      key: 'normalReadings',
      render: (_, record) => (
        <b data-cy="interactiveTimeDashboard-normalReadingsCount">
          {record.readingsCount.normal}
        </b>
      ),
      sorter: true,
      sortOrder: getColSortDir('normalReadings'),
    },
    {
      title: t('labels.totalReadings'),
      key: 'totalReadings',
      render: (_, record) => (
        <b data-cy="interactiveTimeDashboard-totalReadingsCount">
          {record.readingsCount.atRisk +
            record.readingsCount.critical +
            record.readingsCount.normal}
        </b>
      ),
      sorter: true,
      sortOrder: getColSortDir('totalReadings'),
    },
    {
      title: t('labels.totalInteractiveTime'),
      key: 'totalInteractiveTime',
      render: (_, record) => (
        <span data-cy="interactiveTimeDashboard-totalInteractiveTimeCount">
          {convertSecondsToTime(record.interactionTime)}
        </span>
      ),
      sorter: true,
      sortOrder: getColSortDir('totalInteractiveTime'),
    },
    {
      title: t('labels.availableBillableMinutes'),
      key: 'availableBillableMinutes',
      render: (_, record) => (
        <span data-cy="interactiveTimeDashboard-availableBillableMinutes">
          {convertSecondsToTime(
            readingsToBillableSeconds(
              record.readingsCount.unreviewedAgencyReadings,
            ),
          )}
        </span>
      ),
      sorter: true,
      sortOrder: getColSortDir('unreviewedAgencyReadings'),
      sortKey: 'unreviewedAgencyReadings',
    },
    {
      title: t('labels.lastCallDate'),
      key: 'lastPhoneCall',
      width: '105px',
      render: (_, record) => (
        <span
          className={s.lastPhoneCall}
          data-cy="interactiveTimeDashboard-lastPhoneCall"
        >
          {record.lastPhoneCall &&
            DateFormatter({ date: record.lastPhoneCall })}
        </span>
      ),
      sorter: true,
      sortOrder: getColSortDir('lastPhoneCall'),
    },
    {
      title: t('labels.interactionFlag'),
      key: 'interactionFlag',
      render: (_, record) => {
        return <InteractionFlag record={record} />;
      },
    },
    {
      title: t('labels.assignee'),
      key: 'assignee',
      hidden: showAssigneeFilter === false,
      render: (_, record) => <Assignee interactiveTimeItem={record} />,
    },
  ];
};
